import React from 'react';
import { Alert, Button, TextField } from '@mui/material';
import { FormDialog } from '../../../../../components/form-dialog';
import type { ManageServiceProviderEventDialogEvent } from './event-dialog-types';
import { EventComment } from '../event-comment';

interface CancelEventDialogProps {
  event: ManageServiceProviderEventDialogEvent;
  onClose: () => void;
  onBack: () => void;
  onSubmit: (
    eventId: string,
    comment?: string,
    consumedMinutes?: number
  ) => Promise<void>;
}

export function CancelEventDialog(props: CancelEventDialogProps) {
  const { event, onClose, onBack, onSubmit } = props;
  const fallbackConsumedMinutes = React.useMemo(() => {
    const startAtMs = event.startAt.getTime();
    const endAtMs = event.endAt.getTime();
    if (!Number.isFinite(startAtMs) || !Number.isFinite(endAtMs)) {
      return '';
    }

    const plannedMinutes = Math.max(
      0,
      Math.round((endAtMs - startAtMs) / 60000)
    );
    return plannedMinutes.toString();
  }, [event.endAt, event.startAt]);

  const [comment, setComment] = React.useState('');
  const [consumedMinutes, setConsumedMinutes] = React.useState(
    event.consumedMinutes?.toString() ?? fallbackConsumedMinutes
  );
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  function handleClose() {
    if (isSubmitting) {
      return;
    }

    onClose();
  }

  async function handleSubmit() {
    const consumedMinutesValue = consumedMinutes.trim();
    const parsedConsumedMinutes =
      consumedMinutesValue.length > 0 ? Number(consumedMinutesValue) : undefined;

    if (
      consumedMinutesValue.length > 0 &&
      (parsedConsumedMinutes === undefined ||
        !Number.isInteger(parsedConsumedMinutes) ||
        parsedConsumedMinutes < 0)
    ) {
      setError('Brugt tid skal være et heltal på 0 eller mere.');
      return;
    }

    setIsSubmitting(true);
    setError(null);
    try {
      await onSubmit(
        event.eventId,
        comment.trim() || undefined,
        parsedConsumedMinutes
      );
      onClose();
    } catch {
      setError('Kunne ikke aflyse begivenheden. Prøv igen.');
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <FormDialog
      open
      title="Aflys begivenhed"
      confirmButtonText="Aflys"
      cancelButtonText="Luk"
      onClose={handleClose}
      onCancelClick={handleClose}
      onConfirmClick={handleSubmit}
      isLoading={isSubmitting}
      fullWidth
      maxWidth="sm"
      extraDialogAction={
        <Button variant="outlined" onClick={onBack} disabled={isSubmitting}>
          Tilbage
        </Button>
      }
    >
      {error && (
        <Alert severity="error" sx={{ mt: 1 }}>
          {error}
        </Alert>
      )}
      <TextField
        label="Brugt tid (i minutter)"
        value={consumedMinutes}
        onChange={eventValue => setConsumedMinutes(eventValue.target.value)}
        margin="normal"
        fullWidth
        type="number"
        inputProps={{ min: 0, step: 1 }}
      />
      <EventComment comment={comment} onCommentChange={setComment} />
    </FormDialog>
  );
}
